import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { buildApiUrl } from "../config/api";

const RECORD_TYPES = ["A", "CNAME", "MX", "TXT"];

const TYPE_COLORS = {
  A: "text-sky-300 border-sky-400/30 bg-sky-500/10",
  CNAME: "text-violet-300 border-violet-400/30 bg-violet-500/10",
  MX: "text-amber-300 border-amber-400/30 bg-amber-500/10",
  TXT: "text-emerald-300 border-emerald-400/30 bg-emerald-500/10",
};

const defaultForm = {
  type: "A",
  host: "",
  value: "",
  ttl: "7200",
  priority: "10",
};

export default function ManageDns() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [domain, setDomain] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const [deletingKey, setDeletingKey] = useState(null);

  const token = localStorage.getItem("token");

  const fetchRecords = useCallback(async () => {
    if (!token) {
      navigate("/login?redirect=/my-domains", { replace: true });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(buildApiUrl(`/api/domains/${id}/dns`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Unable to load DNS records.");
      }

      setDomain(data.domain || "");
      setRecords(data.records || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (!form.host.trim() || !form.value.trim()) {
      setSubmitError("Host and value are required.");
      return;
    }

    const requestedTtl = Number(form.ttl) || 7200;
    if (requestedTtl < 7200) {
      setSubmitError("TTL must be at least 7200 seconds.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(buildApiUrl(`/api/domains/${id}/dns`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "add",
          type: form.type,
          host: form.host.trim(),
          value: form.value.trim(),
          ttl: requestedTtl,
          priority: Number(form.priority) || 10,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to add record.");
      }

      setSubmitSuccess(`${form.type} record added successfully.`);
      setForm(defaultForm);
      await fetchRecords();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (rec) => {
    const key = `${rec.type}-${rec.host}-${rec.value}`;
    setDeletingKey(key);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const res = await fetch(buildApiUrl(`/api/domains/${id}/dns`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "delete",
          type: rec.type,
          host: rec.host,
          value: rec.value,
          ttl: rec.ttl || 7200,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to delete record.");
      }

      setSubmitSuccess(`${rec.type} record deleted.`);
      await fetchRecords();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-violet-950 to-slate-900 px-6 py-28 text-white">
      <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-violet-500/20 blur-3xl" />
      <div className="absolute bottom-10 right-10 h-96 w-96 rounded-full bg-cyan-500/20 blur-3xl" />

      <div className="mx-auto max-w-5xl">
        {/* HEADER */}
        <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate("/my-domains")}
              className="mb-4 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:bg-white/10"
            >
              ← My Domains
            </button>
            <h1 className="text-4xl font-black md:text-5xl">
              Manage DNS
              {domain ? (
                <span className="ml-3 bg-gradient-to-r from-violet-400 to-cyan-300 bg-clip-text text-transparent">
                  {domain}
                </span>
              ) : null}
            </h1>
            <p className="mt-3 text-slate-400">
              Add, view, and delete A, CNAME, MX, and TXT records for your domain.
            </p>
          </div>
        </div>

        {/* STATUS BANNERS */}
        {submitSuccess ? (
          <div className="mb-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-5 py-3 text-sm font-semibold text-emerald-300">
            {submitSuccess}
          </div>
        ) : null}

        {submitError ? (
          <div className="mb-6 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-5 py-3 text-sm font-semibold text-rose-300">
            {submitError}
          </div>
        ) : null}

        {/* ADD RECORD FORM */}
        <div className="mb-10 rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
          <h2 className="mb-5 text-lg font-black text-white">Add DNS Record</h2>
          <form onSubmit={handleAdd} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Type */}
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-400">
                Type
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm font-semibold text-white outline-none focus:border-violet-400"
              >
                {RECORD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Host */}
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-400">
                Host / Name
              </label>
              <input
                type="text"
                value={form.host}
                onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                placeholder={`e.g. ${form.type === "CNAME" ? "www" : "@"}`}
                className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
              />
            </div>

            {/* Value */}
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-400">
                {form.type === "MX" ? "Mail Server" : form.type === "TXT" ? "Value" : "Points To"}
              </label>
              <input
                type="text"
                value={form.value}
                onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                placeholder={
                  form.type === "A"
                    ? "e.g. 1.2.3.4"
                    : form.type === "CNAME"
                    ? "e.g. target.example.com"
                    : form.type === "MX"
                    ? "e.g. mail.example.com"
                    : "e.g. v=spf1 include:..."
                }
                className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
              />
            </div>

            {/* TTL */}
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-400">
                TTL (seconds)
              </label>
              <input
                type="number"
                value={form.ttl}
                min="7200"
                max="86400"
                onChange={(e) => setForm((f) => ({ ...f, ttl: e.target.value }))}
                className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none focus:border-violet-400"
              />
            </div>

            {/* Priority — MX only */}
            {form.type === "MX" ? (
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-400">
                  Priority
                </label>
                <input
                  type="number"
                  value={form.priority}
                  min="1"
                  max="100"
                  onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none focus:border-violet-400"
                />
              </div>
            ) : null}

            {/* Submit */}
            <div className="flex items-end sm:col-span-2 lg:col-span-1">
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-cyan-600 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:scale-105 disabled:opacity-60"
              >
                {submitting ? "Adding…" : `Add ${form.type} Record`}
              </button>
            </div>
          </form>
        </div>

        {/* RECORDS TABLE */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-violet-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 p-6 text-rose-300">
            {error}
          </div>
        ) : records.length === 0 ? (
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-10 text-center backdrop-blur-xl">
            <p className="text-xl font-black text-slate-300">No DNS records found.</p>
            <p className="mt-2 text-sm text-slate-500">
              Add your first record using the form above.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-[28px] border border-white/10 bg-white/5 backdrop-blur-xl">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="border-b border-white/10 bg-white/5">
                  <tr>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">
                      Type
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">
                      Host
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">
                      Value
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">
                      TTL
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">
                      Priority
                    </th>
                    <th className="px-5 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {records.map((rec, idx) => {
                    const key = `${rec.type}-${rec.host}-${rec.value}`;
                    const colorClass =
                      TYPE_COLORS[rec.type] ||
                      "text-slate-300 border-slate-600 bg-slate-800/30";
                    const isDeleting = deletingKey === key;

                    return (
                      <tr
                        key={`${key}-${idx}`}
                        className="border-b border-white/5 last:border-b-0"
                      >
                        <td className="px-5 py-4">
                          <span
                            className={`rounded-full border px-3 py-1 text-xs font-black uppercase ${colorClass}`}
                          >
                            {rec.type}
                          </span>
                        </td>
                        <td className="px-5 py-4 font-mono text-sm text-white">
                          {rec.host || "@"}
                        </td>
                        <td className="max-w-xs break-all px-5 py-4 font-mono text-sm text-slate-300">
                          {rec.value}
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-400">{rec.ttl}</td>
                        <td className="px-5 py-4 text-sm text-slate-400">
                          {rec.type === "MX" ? (rec.priority ?? "-") : "-"}
                        </td>
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            disabled={isDeleting}
                            onClick={() => handleDelete(rec)}
                            className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-2 text-xs font-bold text-rose-300 transition hover:bg-rose-500/20 disabled:opacity-50"
                          >
                            {isDeleting ? "Deleting…" : "Delete"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
